
import React, { useState } from 'react';
import { AppConfig, Language, Dialect } from '../../types';
import { TRANSLATIONS, DIALECTS, VOICES } from '../../constants';
import { audioService } from '../../services/audioService';
import { generateAudio } from '../../services/geminiService';

interface AppPrefsTabProps {
  config: AppConfig;
  onUpdate: (config: Partial<AppConfig>) => void;
  onRestartTour?: () => void;
}

const AppPrefsTab: React.FC<AppPrefsTabProps> = ({ config, onUpdate, onRestartTour }) => {
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const t = TRANSLATIONS[config.language];
  const isRtl = config.language === 'ar';

  const speedPresets = [
    { label: isRtl ? 'بطيء' : 'Slow', value: 0.8 },
    { label: isRtl ? 'عادي' : 'Normal', value: 1.0 },
    { label: isRtl ? 'سريع' : 'Fast', value: 1.2 },
    { label: isRtl ? 'فائق' : 'Max', value: 1.5 },
  ];

  const handlePreviewVoice = async (voiceId: string) => {
    if (previewingVoice) return;
    setPreviewingVoice(voiceId);
    try {
      const previewText = config.language === 'ar' ? "يا هلا بك، أنا صوت مساعد لهجة الذكي." : "Welcome, I am your smart assistant voice.";
      const base64 = await generateAudio(previewText, voiceId, config.apiKey);
      if (base64) {
        await audioService.playPCM(base64, config.speechSpeed);
      }
    } catch (err) {
      console.error("Preview failed", err);
    } finally {
      setPreviewingVoice(null);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-6">
      
      {/* Toggles Group */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
         {/* Voice Toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-100 dark:border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500">
              <i className="fas fa-volume-up text-xs"></i>
            </div>
            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{t.enableTTS}</span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={config.ttsEnabled} onChange={(e) => onUpdate({ ttsEnabled: e.target.checked })} className="sr-only peer" />
            <div className="w-9 h-5 bg-slate-200 dark:bg-white/10 rounded-full peer-checked:after:translate-x-full peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>

        {/* Theme Toggle */}
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-100 dark:border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500">
              <i className={`fas ${config.theme === 'dark' ? 'fa-moon' : 'fa-sun'} text-xs`}></i>
            </div>
            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{t.theme}</span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={config.theme === 'dark'} onChange={(e) => onUpdate({ theme: e.target.checked ? 'dark' : 'light' })} className="sr-only peer" />
            <div className="w-9 h-5 bg-slate-200 dark:bg-white/10 rounded-full peer-checked:after:translate-x-full peer-checked:bg-indigo-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>
      </div>

      {/* Language */}
      <div>
        <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">{t.language}</h3>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onUpdate({ language: 'ar' })} className={`py-2.5 rounded-xl border text-[10px] font-black transition-all ${config.language === 'ar' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500'}`}>العربية</button>
          <button onClick={() => onUpdate({ language: 'en' })} className={`py-2.5 rounded-xl border text-[10px] font-black transition-all ${config.language === 'en' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500'}`}>English</button>
        </div>
      </div>

      {/* Dialect */}
      <div>
        <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">{t.dialect}</h3>
        <div className="grid grid-cols-2 gap-2">
          {DIALECTS.map((d) => (
            <button
              key={d.id}
              onClick={() => onUpdate({ dialect: d.id as Dialect })}
              className={`
                flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-[0.98]
                ${config.dialect === d.id 
                  ? 'bg-emerald-500/5 border-emerald-500/50 text-emerald-600 dark:text-emerald-400' 
                  : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500'}
              `}
            >
              <span className="text-lg">{d.icon}</span>
              <span className="text-[10px] font-black">{d.name[config.language]}</span>
              {config.dialect === d.id && <i className={`fas fa-check-circle text-emerald-500 text-[10px] ${isRtl ? 'mr-auto' : 'ml-auto'}`}></i>}
            </button>
          ))}
        </div>
      </div>

      {/* Voice & Speed */}
      <div>
        <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">{t.voice} & {t.speed}</h3>
        <div className="bg-slate-50 dark:bg-white/5 rounded-2xl p-4 border border-slate-100 dark:border-white/5 space-y-4">
          
          {/* Voice Grid */}
          <div className="grid grid-cols-2 gap-2">
            {VOICES.map((v) => (
              <div 
                key={v.id} 
                onClick={() => onUpdate({ voice: v.id })} 
                className={`
                  relative flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all
                  ${config.voice === v.id ? 'bg-white dark:bg-black/20 border-emerald-500 shadow-sm' : 'border-transparent hover:bg-white dark:hover:bg-white/5'}
                `}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs ${v.gender === 'male' ? 'bg-blue-500/10 text-blue-500' : 'bg-pink-500/10 text-pink-500'}`}>
                  <i className={`fas ${v.gender === 'male' ? 'fa-mars' : 'fa-venus'}`}></i>
                </div>
                <p className={`text-[10px] font-black truncate flex-1 ${config.voice === v.id ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-400'}`}>{v.label[config.language].split('(')[0]}</p>
                <button onClick={(e) => { e.stopPropagation(); handlePreviewVoice(v.id); }} className="w-6 h-6 rounded-md flex items-center justify-center bg-slate-100 dark:bg-white/10 text-slate-400 hover:text-emerald-500 transition-colors">
                  <i className={`fas ${previewingVoice === v.id ? 'fa-spinner fa-spin' : 'fa-play'} text-[8px]`}></i>
                </button>
              </div>
            ))}
          </div>

          {/* Speed Slider */}
          <div className="pt-2 border-t border-slate-200 dark:border-white/5">
             <div className="flex justify-between items-center mb-2">
                <span className="text-[9px] font-bold text-slate-400">{t.speed}</span>
                <span className="text-[9px] font-black text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded">{config.speechSpeed.toFixed(1)}x</span>
             </div>
             <input type="range" min="0.5" max="2" step="0.1" value={config.speechSpeed} onChange={(e) => onUpdate({ speechSpeed: parseFloat(e.target.value) })} className="w-full h-1.5 bg-slate-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-emerald-500" />
             <div className="flex justify-between mt-2">
                {speedPresets.map(preset => (
                  <button key={preset.value} onClick={() => onUpdate({ speechSpeed: preset.value })} className={`text-[8px] font-bold px-2 py-1 rounded transition-colors ${config.speechSpeed === preset.value ? 'bg-emerald-500 text-white' : 'text-slate-400 bg-slate-200/50 dark:bg-white/5'}`}>{preset.label}</button>
                ))}
             </div>
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-slate-100 dark:border-white/5">
         <button onClick={onRestartTour} className="w-full py-3 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 transition-all">
            <i className="fas fa-magic"></i>
            {t.restartTour}
         </button>
      </div>
    </div>
  );
};
export default AppPrefsTab;
